import React from 'react';
import { arrayOf, func, node, number, object, shape, string } from 'prop-types';
import { FormattedMessage, injectIntl } from '../../../../util/reactIntl';
import { FieldTextInput, IconSpinner, LinkedLogo, Menu, MenuContent, MenuItem, MenuLabel, NamedLink, PrimaryButton } from '../../../../components';

import IconFacebook from './icons/IconFacebook';
import IconTweeter from './icons/IconTweeter';
import IconLinkedin from './icons/IconLinkedin';
import IconPinterest from './icons/IconPinterest';
import IconInstagram from './icons/IconInstagram';
import IconYoutube from './icons/IconYoutube';


import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import { combinedAccount, userTypes } from '../../../../util/userTypes';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { required } from '../../../../util/validators';
import { post } from '../../../../util/api';
import { fetchCurrentUser } from '../../../../ducks/user.duck';

const sharetribeSdk = require('sharetribe-flex-sdk');
const sdk = sharetribeSdk.createInstance({
  clientId: process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID,
});


const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SectionFooterComponent = props => {
  const {
    sectionId,
    className,
    rootClassName,
    appearance,
    options,
    linkLogoToExternalSite,
    currentUser,
  } = props;

  const dispatch = useDispatch();

  const [subscribedToNewsletter, setSubscribedToNewsletter] = useState(currentUser?.attributes?.profile?.privateData?.isSubscribedToNewsletter);
  const [isBusy, setIsBusy] = useState(false);


  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  const subscribeToEmail = (values) => {
    setIsBusy(true);
    post('/api/subscribe-to-newsletter', { email: values.email })
      .then(response => {
        if (currentUser) {
          return sdk.currentUser
            .updateProfile({
              privateData: {
                isSubscribedToNewsletter: true,
              },
            })
            .then(resp => {
              setSubscribedToNewsletter(true);
              setIsBusy(false);
              dispatch(fetchCurrentUser());
            })
            .catch(e => console.log(e));
        } else {
          setSubscribedToNewsletter(true);
          setIsBusy(false);
        }

      })
      .catch(e => {
        setIsBusy(false);
      });
  }

  const unsubscribeFromEmail = () => {
    setIsBusy(true);
    if (currentUser) {
      post('/api/unsubscribe-from-newsletter', { email: currentUser.attributes.email })
        .then(response => {

          return sdk.currentUser
            .updateProfile({
              privateData: {
                isSubscribedToNewsletter: false,
              },
            })
            .then(resp => {
              setSubscribedToNewsletter(false);
              setIsBusy(false);
              dispatch(fetchCurrentUser());
            })
            .catch(e => console.log(e));
        })
        .catch(e => {
          setIsBusy(false);
        });
    } else {
      setSubscribedToNewsletter(false);
      setIsBusy(false);
    }
  }

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={css.content}>
          <div className={css.col1}>
            <div>
              <LinkedLogo
                rootClassName={css.logoLink}
                logoClassName={css.logoWrapper}
                logoImageClassName={css.logoImage}
                linkToExternalSite={linkLogoToExternalSite}
                layout={logoLayout}
              />
              <p className={css.slogan}>
                <FormattedMessage id="SectionFooter.slogan" />
              </p>
            </div>
            <div className={css.icons}>
              <IconFacebook />
              <IconTweeter />
              <IconLinkedin />
              <IconInstagram />
              <IconPinterest />
              <IconYoutube />
            </div>
            <div className={css.emailWrapper}>
              <FinalForm
                onSubmit={(values) => {
                  subscribeToEmail(values)
                }
                }
                formId="submit"
                render={fieldRenderProps => {
                  const { formId, handleSubmit, invalid, pristine, submitting } = fieldRenderProps;
                  const submitDisabled = invalid || pristine || submitting || subscribedToNewsletter;
                  return (
                    <form onSubmit={handleSubmit}>
                      <div className={css.emailInput}>

                        {subscribedToNewsletter ?
                          <span>
                            <FormattedMessage id="SectionFooter.subscribed" />
                            <span onClick={(e) => {
                              e.preventDefault();
                              unsubscribeFromEmail();
                            }}
                              className={css.link}>
                              {isBusy ? <IconSpinner style={{ height: "6px", width: "6px" }} />
                                : <FormattedMessage id="SectionFooter.unsubscribe" />
                              }
                            </span>
                          </span>
                          :
                          <div className='col'>
                            <FormattedMessage id="SectionFooter.subscribeToNewsletter" />

                            <div className='row mt10 gap5'>
                              <FieldTextInput
                                name="email"
                                id="email"
                                type="email"
                                placeholder="Email address"
                                validate={required('This field is required')}
                              />
                              <PrimaryButton
                                inProgress={isBusy}
                                className={css.submit}
                                disabled={submitDisabled}>
                                Subscribe
                              </PrimaryButton>
                            </div>
                          </div>
                        }
                      </div>

                    </form>
                  );
                }}
              />

            </div>
          </div>
          <div>
            <ul>
              <li className={css.listHeader}>Help</li>
              <li>
                <NamedLink name="WhatIsHouseSittingPage">How it works</NamedLink>
              </li>

              <li>
                <NamedLink
                  name="MembershipPlansPage"
                  params={{ accountType: userTypes.homeowner }}
                >
                  <FormattedMessage id="SectionFooter.pricing.homeowner" />
                </NamedLink>
              </li>
              <li>
                <NamedLink
                  name="MembershipPlansPage"
                  params={{ accountType: userTypes.housesitter }}
                >
                  <FormattedMessage id="SectionFooter.pricing.housesitter" />
                </NamedLink>
              </li>
              <li>
                <NamedLink
                  name="MembershipPlansPage"
                  params={{ accountType: combinedAccount }}
                >
                  <FormattedMessage id="SectionFooter.pricing.combinedAccount" />
                </NamedLink>
              </li>
              {/* <li>
                <Menu>
                  <MenuLabel className={css.dropdownLinkLabel}>
                    Pricing
                  </MenuLabel>

                  <MenuContent className={css.dropdownContent}>
                    <MenuItem key="Homeowner">
                      <NamedLink
                        name="MembershipPlansPage"
                        params={{ accountType: userTypes.homeowner }}
                      >
                        <FormattedMessage id="SectionFooter.pricing.homeowner" />
                      </NamedLink>
                    </MenuItem>
                    <MenuItem key="Housesitter">
                      <NamedLink
                        name="MembershipPlansPage"
                        params={{ accountType: userTypes.housesitter }}
                      >
                        <FormattedMessage id="SectionFooter.pricing.housesitter" />
                      </NamedLink>
                    </MenuItem>
                    <MenuItem key="Combined acount">
                      <NamedLink
                        name="MembershipPlansPage"
                        params={{ accountType: combinedAccount }}
                      >
                        <FormattedMessage id="SectionFooter.pricing.combinedAccount" />
                      </NamedLink>
                    </MenuItem>
                  </MenuContent>
                </Menu>
              </li> */}
              <li>
                <NamedLink name="TrustAndSafetyPage">Trust and safety</NamedLink>
              </li>
              {/* <li>Help desk</li> */}
              {/* <li>Home and Contents Plan</li> */}
              <li>
                <a href="https://uk.trustpilot.com/review/housesitmatch.com" target="_blank">
                  Reviews and Testimonials
                </a>
              </li>
              <li>
                <NamedLink name="HouseSittingVisaPage">Housesitting Visas</NamedLink>
              </li>
              <li>
                <NamedLink name="TripCancellationSupportPage">Trip Cancellation Support</NamedLink>
              </li>
            </ul>
          </div>
          <div>
            <ul>
              <li className={css.listHeader}>Discover more</li>
              <li>
                <NamedLink name="AboutHouseSitMatchPage">About us</NamedLink>
              </li>
              {/* <li>
                <a href="https://www.facebook.com/groups/1471136503122934/" target="_blank">
                  Housesitting community
                </a>
              </li> */}
              <li>
                <a href='/blog/house-sitting-and-sustainable-travel/' target='_blank'>
                  Green Travel and Housesitting
                </a>
              </li>
              <li>
                <a href='/blog' target='_blank'>
                  Housesitting Blogs
                </a>
              </li>
              <li>
                <a href='/contact-us'>
                  Contact Us
                </a>
              </li>
              <li>
                <NamedLink name="PressRoomPage">Press room</NamedLink>
              </li>
              <li>Affiliates</li>
            </ul>
          </div>
          <div>
            <ul>
              <li className={css.listHeader}>Start searching for...</li>
              <li>
                <a href='/petsitting-services' target='_blank'>
                  Pet sitters
                </a>
              </li>
              <li>
                <a href=' /petsitting-services/dog-sitting' target='_blank'>
                  Dog sitters
                </a>
              </li>
              <li>
                <a href='/petsitting-services/cat-sitting' target='_blank'>
                  Cat sitters
                </a>
              </li>
              <li>
                <a href='/search-house-sitter' target='_blank'>
                  Find a Pet sitter
                </a>
              </li>
              <li>
                <a href='/search-house-sit' target='_blank'>
                  Find a long term housesit
                </a>
              </li>
              <li>
                <a href='/house-sitting-uk' target='_blank'>
                  Pet sitters in London
                </a>
              </li>
            </ul>
          </div>

        </div>
        <div className={css.bottomWrapper}>
          <p className={css.copyright}>
            <FormattedMessage id="SectionFooter.copyright" />
          </p>
          <div className={css.links}>
            <NamedLink name="TermsOfServicePage">Terms of Use</NamedLink>
            <NamedLink name="TermsAndConditions">Terms and Conditions of Supply</NamedLink>
            <NamedLink name="PrivacyPolicyPage">Privacy Policy / How we use Cookies</NamedLink>
          </div>
        </div>
      </div>
    </SectionContainer >
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooterComponent.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooterComponent.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return { currentUser };
};

const SectionFooter = compose(
  connect(mapStateToProps),
  injectIntl
)(SectionFooterComponent);

export default SectionFooter;
